<script setup lang="ts">
  import { computed, PropType, ref } from 'vue'
  import { useDisplay } from 'vuetify'
  import { ICharacterCdn, ISkillCdn, skillsList } from '@/store/ClassesJobs'
  const { mdAndUp, mobile } = useDisplay()

  const props = defineProps({
    characterData: {
      type: Object as PropType<ICharacterCdn>,
      required: true,
    },
  })

  const statsOrAbilitiesList = ['Basic Stats', 'Skills & Abilities']
  const statsOrSkillsAbilitiesSelector = ref(statsOrAbilitiesList[0])
  const setStatsOrSkillsAbilitiesSelector = (val: string) => (statsOrSkillsAbilitiesSelector.value = val)
  const skillsAbilitiesSelector = ref(0)
  const selectedSkillIndex = ref(0)
  const setSelectedSkillIndex = (val: number) => (selectedSkillIndex.value = val)
  const setSkillsAbilitiesSelector = (val: number) => {
    setSelectedSkillIndex(0)
    skillsAbilitiesSelector.value = val
  }

  const selectedSkill = computed<ISkillCdn | undefined>(() =>
    props.characterData
      ? ((props.characterData[
          skillsList[skillsAbilitiesSelector.value].fieldName as keyof typeof props.characterData
        ] as ISkillCdn[]) || [])[selectedSkillIndex.value]
      : undefined
  )
</script>
<template>
  <div
    class="statsAbilities"
    :class="{ inverse: statsOrSkillsAbilitiesSelector === statsOrAbilitiesList[1] && !mobile }"
  >
    <div>
      <span v-if="!mdAndUp && !mobile" class="statsAbilitiesTitle">{{ statsOrSkillsAbilitiesSelector }}</span>
      <span v-if="mobile" class="statsAbilitiesTitle">{{ statsOrAbilitiesList[0] }}</span>
      <CJSlider
        v-if="!mobile"
        :options="statsOrAbilitiesList"
        :selected-option="statsOrSkillsAbilitiesSelector"
        @change="setStatsOrSkillsAbilitiesSelector"
      />
      <CJStats
        v-if="(mdAndUp && statsOrSkillsAbilitiesSelector === statsOrAbilitiesList[0]) || mobile"
        :character-data="props.characterData"
      />
      <div
        v-if="!mdAndUp && !mobile && statsOrSkillsAbilitiesSelector === statsOrAbilitiesList[1] && selectedSkill"
        class="selected-skill"
      >
        <div>{{ selectedSkill.title }}</div>
        <div>{{ selectedSkill.description }}</div>
      </div>
      <div v-if="!mdAndUp && !mobile" class="divider"></div>
    </div>
    <div>
      <span v-if="mobile" class="statsAbilitiesTitle">{{ statsOrAbilitiesList[1] }}</span>
      <CJStats
        v-if="!mdAndUp && statsOrSkillsAbilitiesSelector === statsOrAbilitiesList[0] && !mobile"
        :character-data="props.characterData"
      />
      <CJSkillsAbilities
        v-if="statsOrSkillsAbilitiesSelector === statsOrAbilitiesList[1] || mobile"
        :character-data="props.characterData"
        :selected-option="skillsAbilitiesSelector"
        :selected-skill="selectedSkillIndex"
        @change="setSkillsAbilitiesSelector"
        @change:skill="setSelectedSkillIndex"
      >
        <template v-if="mobile && selectedSkill" #skill>
          <div class="selected-skill">
            <div>{{ selectedSkill.title }}</div>
            <div>{{ selectedSkill.description }}</div>
          </div>
        </template>
      </CJSkillsAbilities>
    </div>
  </div>
</template>
<style lang="scss" scoped>
  .statsAbilities {
    height: pxToRem(403.225px);
    width: pxToRem(483.875px);
    border: pxToRem(2.075px) solid #fff;
    border-radius: pxToRem(6.25px);
    overflow: hidden;
    background-color: rgba(255, 255, 255, 0.75);
    display: flex;
    flex-direction: column;
    > div:nth-child(2) {
      height: 100%;
    }
  }

  @media #{$tablet} {
    div.statsAbilities {
      margin-top: pxToRemTablet(38px);
      width: pxToRemTablet(959.994px);
      background-color: #dcdcdc;
      border-radius: pxToRemTablet(3.8px);
      flex-direction: row;
      min-height: pxToRemTablet(342.228px);
      height: pxToRemTablet(342.228px);
      transition: all 0.2s linear;
      &.inverse {
        background-color: #2f2f2f;
        border-color: #636363;
        height: auto;
        .statsAbilitiesTitle {
          color: #c0c0c0;
        }
        .divider {
          background-color: rgba(255, 255, 255, 0.5);
        }
      }
      > div:first-of-type {
        display: flex;
        min-width: pxToRemTablet(437.57px);
        width: pxToRemTablet(437.57px);
        position: relative;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: pxToRemTablet(38px) 0;
      }
      > div:nth-child(2) {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
      }
      .statsAbilitiesTitle {
        color: #515151;
        font-family: Glegoo;
        font-size: pxToRemTablet(35.986px);
        font-weight: bold;
        height: auto;
      }
      .selected-skill {
        font-size: pxToRemTablet(18.012px);
        color: #fff;
        text-align: center;
        margin-top: pxToRemTablet(19px);
        padding: 0 pxToRemTablet(28.5px);
        > div:first-child {
          font-weight: bold;
          margin-bottom: pxToRemTablet(9.5px);
        }
      }
      .divider {
        position: absolute;
        width: pxToRemTablet(0.988px);
        height: calc(100% - pxToRemTablet(66.5px));
        background-color: #000;
        right: 0;
        top: 50%;
        transform: translateY(-50%);
        transition: all 0.2s linear;
      }
    }
  }

  @media #{$mobile} {
    div.statsAbilities {
      background-color: transparent;
      border: none;
      flex-direction: column;
      height: auto;
      width: pxToRemMobile(550.008px);
      margin-top: pxToRemMobile(9px);
      .statsAbilitiesTitle {
        color: #515151;
        font-size: pxToRemMobile(36px);
        margin-top: pxToRemMobile(9px);
      }
      > div:first-of-type {
        padding: 0;
        justify-content: flex-start;
        background-color: #ebebeb !important;
        width: 100%;
        // height: pxToRemMobile(325.998px);
        border-radius: pxToRemMobile(4.5px);
        padding-bottom: 1.5rem;
      }
      > div:nth-child(2) {
        border-radius: pxToRemMobile(4.5px);
        margin-top: pxToRemMobile(27px);
        background-color: #2f2f2f;
        flex-direction: column;
        .statsAbilitiesTitle {
          color: #fff;
          margin-bottom: pxToRemMobile(13.5px);
        }
      }
      .selected-skill {
        font-size: pxToRemMobile(18px);
        padding: 0 pxToRemMobile(40.5px);
        > div:first-child {
          margin-bottom: pxToRemMobile(1.8px);
        }
      }
    }
  }
</style>
